import type { CSSProperties, PropsWithChildren } from 'react';
import { forwardRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { useStableRect } from '@elfsight/widget-utils';
import useMergedRef from '@react-hook/merged-ref';

export type MediaContainerProps = PropsWithChildren<{
  width?: string;
  height?: string;
  ratio?: number;
  backgroundColor?: string;
  backgroundImage?: string;
  showBackgroundOnLoad?: boolean;
  borderRadius?: number;
  style?: CSSProperties;
  adaptiveMedia?: boolean;
  setMediaHeight?(height: number): void;
}>;

export const MediaContainer = forwardRef<HTMLDivElement, MediaContainerProps>(
  function MediaContainer(
    {
      children,
      width = '100%',
      height = '100%',
      ratio,
      backgroundColor,
      backgroundImage,
      showBackgroundOnLoad = false,
      borderRadius,
      style,
      adaptiveMedia,
      setMediaHeight,
      ...forwardingProps
    },
    forwardingRef
  ) {
    const [backgroundLoaded, setBackgroundLoaded] = useState(!backgroundImage);
    const handleBackgroundReady = () => {
      setBackgroundLoaded(true);
    };

    const [realWidth, setRealWidth] = useState(0);
    const [measureRef] = useStableRect(
      { width: realWidth },
      ({ width, height }) => {
        setRealWidth(width);
        setMediaHeight?.(height);
      }
    );

    const mergedRef = useMergedRef(forwardingRef, measureRef);
    const adaptiveMediaHeight = adaptiveMedia ? 'auto' : height;
    const containerHeight = ratio ? 0 : adaptiveMediaHeight;

    // TODO: Необходимо аккуратно переехать на другое решение c paddingTop, которое исключает использование JS - https://chatgpt.com/share/7ebdc7ca-8f6f-434a-b709-911ff2eb9477
    // Внутри Container необходимо добавить еще обертку с width: 100%, где paddingTop = ratio ? ratio * 100% : 0
    // Такое решение позволит нам избавится от проблем, связанных с использованием JS для расчета ширины, например https://elfsight.atlassian.net/browse/NWD-390
    // Стоит учесть все кейсы использования компонента в разных продуктах, тк меняется меняется семантика
    // Так же можно глянуть решение через aspect-ratio: https://developer.mozilla.org/en-US/docs/Web/CSS/aspect-ratio

    // upd: aspect-ratio работает, но его не внедрить, тк мы изначально в продуктах неправильно указывали ratio - height / width, а надо наоброт
    // это надо будет исправить после переезда всех продуктов в новую инфраструктуру https://elfsight.atlassian.net/browse/WIDGETS-2509

    return (
      <Container
        {...forwardingProps}
        ref={mergedRef}
        _width={width}
        _borderRadius={borderRadius}
        _backgroundColor={
          backgroundLoaded || showBackgroundOnLoad ? backgroundColor : undefined
        }
        style={{
          ...style,
          paddingTop:
            ratio && realWidth ? `${Math.round(realWidth * ratio)}px` : 0,
          height: containerHeight
        }}
      >
        {backgroundImage && (
          <BackgroundImage
            src={backgroundImage}
            onLoad={handleBackgroundReady}
            onError={handleBackgroundReady}
          />
        )}
        {ratio ? <Inner>{children}</Inner> : children}
      </Container>
    );
  }
);

const Container = styled.div<{
  _width?: string;
  _borderRadius?: number;
  _backgroundColor?: string;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  width: ${({ _width }) => _width};
  ${({ _borderRadius }) =>
    _borderRadius &&
    css`
      border-radius: ${_borderRadius}px;
    `};

  ${({ _backgroundColor }) =>
    _backgroundColor &&
    css`
      background-color: ${_backgroundColor};
    `}
`;

const BackgroundImage = styled.img`
  position: absolute;
  width: 10%;
  height: 10%;
  filter: blur(2px);
  left: 45%;
  top: 45%;
  transform: scale(11);
  opacity: 0.3;
  object-fit: cover;
`;

const Inner = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: center;
`;
